/// <reference path="../ledgeraccount/ReconciliationEntryState.ts" />
/// <reference path="AccountingEntryCell.ts" />
/// <reference path="AccountingEntryTotalsCell.ts" />

enum AccountEntintyType 
{
    SalesInvoice,
    PurchaseInvoice,
    Client,
    Supplier,
    Loan
}

interface AccountingEntryDelegate 
{
    addClientAccountEntry(sender:any, client:Client) : void;
    addSupplierAccountEntry(sender:any, supplier:Supplier) : void;
    addPurchaseInvoiceAccountEntry(sender:any, purchaseInvoices:PurchaseInvoice[]) : void;
    addInstallmentAccountEntry(sender:any, installment:PaymentInstallment, save:boolean) : void;

    linkInstalmentsFromBankFile(sender:any, file:BankTransferBundleFile) : void;
}

// The accounting entries panel of the ledger account reconciliation, for a document or a bank
// movement. Without a journal entry it offers to generate one (and a line can still be added by
// hand); a draft entry is edited line by line and locked once it is balanced; a locked one is
// only shown until it is unlocked. The status bar says which of these the entry is in, and the
// list is told whenever the panel generates, locks, unlocks or discards an entry.
class AccountingEntryViewController extends MUIViewController implements AccountingEntryCellDelegate, AccountingEntryDelegate
{    
    static newInstance() : AccountingEntryViewController {
        let vc = new AccountingEntryViewController("accounting-entry-view");
        vc.initWithResource("layout/accounting_entries/AccountingEntryView.html");
        return vc;
    }

    // the table's sections: the entry's lines, their totals, the new line
    private static readonly linesSection = 0;
    private static readonly totalsSection = 1;
    private static readonly addSection = 2;
    
    private lockButton:MUIButton = null;
    private unlockButton:MUIButton = null;
    private optionsButton:MUIButton = null;

    private entryNumber:MUILabel = null;
    private dateTextField:MUITextField = null;
    private dateButton:MUIButton = null;
    private analyticAccountDropdown:MUIButton = null;
    private linkTitleLabel:MUILabel = null;
    private linkEntityLabel:MUILabel = null;
    private linkAmountLabel:MUILabel = null;
    private linkLegalEntityLabel:MUILabel = null;
    private linkButton:MUIButton = null;
    private viewLinksButton:MUIButton = null;
    private differencesView:MUIView = null;

    private emptyView:MUIView = null;
    private generateButton:MUIButton = null;

    private tableView:UITableView = null;

    private statusDot:MUIView = null;
    private statusLabel:MUILabel = null;

    private footerCotainer:MUIView = null;
    private accountingSelectionEntityViewController:AccountingEntitySelectionViewController = null;

    // the date picked before the entry exists: the entry takes it when it is created
    private conciliation_date:Date = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.lockButton = MUIOutlet(this, "lock-btn", "MUIButton");
        this.lockButton.setAction(this, this.lockEntries);

        this.optionsButton = MUIOutlet(this, "action-btn", "MUIButton");
        this.optionsButton.setAction(this, this.optionsAction); 

        this.unlockButton = MUIOutlet(this, "unlock-btn", "MUIButton");
        this.unlockButton.setAction(this, this.unlockEntries);

        this.entryNumber = MUIOutlet(this, "entry-num-lbl", "MUILabel");

        // the date is picked from the calendar
        this.dateTextField = MUIOutlet(this, "date-tf", "MUITextField");
        let dateInput = MUILayerGetFirstElementWithTag(this.dateTextField.layer, "INPUT") as HTMLInputElement;
        if (dateInput != null) dateInput.readOnly = true;
        this.dateButton = MUIOutlet(this, "date-btn", "MUIButton");
        this.dateButton.setAction(this, this.showDatePicker);

        this.analyticAccountDropdown = MUIOutlet(this, "analytic-account-dd", "MUIButton");
        this.analyticAccountDropdown.setAction(this, this.showSelectAnalyticAccountViewController);
        this.linkTitleLabel = MUIOutlet(this, "link-title-lbl", "MUILabel");
        this.linkEntityLabel = MUIOutlet(this, "link-entity-lbl", "MUILabel");
        this.linkAmountLabel = MUIOutlet(this, "link-amount-lbl", "MUILabel");
        this.linkLegalEntityLabel = MUIOutlet(this, "link-legal-entity-lbl", "MUILabel");
        this.linkButton = MUIOutlet(this, "link-btn", "MUIButton");
        this.linkButton.setAction(this, this.showSelectEntityViewController);
        this.viewLinksButton = MUIOutlet(this, "view-links-btn", "MUIButton");
        this.viewLinksButton.setAction(this, this.showDocumentLinks);
        this.differencesView = MUIOutlet(this, "differences-view", "MUIView");

        this.emptyView = MUIOutlet(this, "empty-view", "MUIView");
        this.generateButton = MUIOutlet(this, "generate-btn", "MUIButton");
        this.generateButton.setAction(this, function(this:AccountingEntryViewController){
            if (this.entryState == ReconciliationEntryState.draft) this.confirmRegeneration();
            else this.generateEntries();
        });

        this.statusDot = MUIOutlet(this, "status-dot", "MUIView");
        this.statusLabel = MUIOutlet(this, "status-lbl", "MUILabel");

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;

        this.footerCotainer = MUIOutlet(this, "footer-container", "MUIView");
        
        this.accountingSelectionEntityViewController = AccountingEntitySelectionViewController.newInstance();
        this.accountingSelectionEntityViewController.delegate = this;
        this.addChildViewController(this.accountingSelectionEntityViewController);
        this.footerCotainer.addSubview(this.accountingSelectionEntityViewController.view);        
    }

    viewWillAppear(animated?: boolean): void {
        super.viewWillAppear(animated);
        this.updateUI();
    }

    private _journal_entry:AccountingJournalEntry = null;
    private _remainig_amount:number = 0;
    private _item:CoreAccounting = null;
    // the lines of an invoice booked one by one, before the entries were made per invoice
    private _legacy_lines:CoreAccounting[] = [];
    // the user answered "not now" to generating this item's entry again after a change
    private _regeneration_declined = false;

    set item(value:CoreAccounting) {
        this.setItem(value, []);
    }

    /// The item, and for an invoice the lines the list shows of it (some may have been booked
    /// one by one: their entries are shown, locked and replaced with the invoice's).
    setItem(value:CoreAccounting, lines:CoreAccounting[]) {
        this._item = value;
        this._legacy_lines = ReconciliationEntryStateHelper.linesBookedOneByOne(this.accountingSource, lines);
        this.loadLinesBookedOneByOne();
        
        // Manual entries hang on the selected item; generated ones on its document
        this._journal_entry = this._item.journalEntry ?? this.accountingSource?.journalEntry ?? this._legacy_lines[0]?.journalEntry ?? null;
        this._remainig_amount = 0;
        this.conciliation_date = null;
        this._regeneration_declined = false;
        this.collapseLinkSelection();
        
        this.updateUI();        
    }

    // The object whose journal entry the rules generate: a document line books
    // through its invoice; bank movements and documents are their own source.
    private get accountingSource(): CoreAccounting {
        if (this._item instanceof DBDocumentLine) return (this._item as DBDocumentLine).document ?? this._item;
        return this._item;
    }

    // The list may be filtered: all the invoice's lines booked one by one come from the server.
    // Until they do, nothing is generated for the invoice.
    private _checking_legacy_lines = false;
    private loadLinesBookedOneByOne() {
        let source = this.accountingSource;
        let lineEntity = source instanceof SalesInvoice ? "SalesInvoiceLine" : (source instanceof PurchaseInvoice ? "PurchaseInvoiceLine" : null);
        this._checking_legacy_lines = false;
        if (lineEntity == null || source.journalEntry != null) return;

        this._checking_legacy_lines = true;
        let predicate = MIOPredicate.predicateWithFormat("document.identifier == '" + source.identifier + "' AND journalEntry != null");
        DBHelper.queryObjectsWithCompletion(lineEntity, null, predicate, ["journalEntry"], this, function(this:AccountingEntryViewController, lines:CoreAccounting[]){
            if (source != this.accountingSource) return;
            this._checking_legacy_lines = false;
            let booked = ReconciliationEntryStateHelper.linesBookedOneByOne(source, lines ?? []);
            let known = this._legacy_lines;
            let changed = booked.length != known.length || booked.some(l => known.indexOf(l) < 0);
            if (changed) {
                this._legacy_lines = booked;
                this._journal_entry = this._item.journalEntry ?? source.journalEntry ?? booked[0]?.journalEntry ?? null;
                this.updateUI();
            }
            else this.updateEntryState();
        });
    }

    private get entryState(): ReconciliationEntryState {
        if (this._item == null) return ReconciliationEntryState.none;
        let source = this.accountingSource;
        if (this._item.journalEntryLocked || source?.journalEntryLocked) return ReconciliationEntryState.locked;
        if (this._legacy_lines.length > 0) return ReconciliationEntryStateHelper.stateForDocument(source, this._legacy_lines);
        if (this._journal_entry?.locked) return ReconciliationEntryState.locked;
        if (this._journal_entry != null) return ReconciliationEntryState.draft;
        return ReconciliationEntryState.none;
    }

    // Every journal entry the panel's lines hang on (the invoice's, or its lines' own)
    private involvedJournalEntries() : AccountingJournalEntry[] {
        let entries:AccountingJournalEntry[] = [];
        let add = (je:AccountingJournalEntry) => { if (je != null && entries.indexOf(je) == -1) entries.push(je); };
        add(this._journal_entry);
        for (let line of this._legacy_lines) add(line.journalEntry);
        for (let e of (this._fetchedResultsController?.fetchedObjects ?? []) as AccountingEntry[]) add(e.journalEntry);
        return entries;
    }

    private updateUI(){
        if (this._item == null || this.viewIsLoaded == false) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let locked = this.entryState == ReconciliationEntryState.locked;

        this.entryNumber.text = this._journal_entry?.journalIndex?.toString() ?? "—";
        this.dateTextField.text = ad.dateFormatter.stringFromDate(this.entryDate());
        this.setButtonEnabled(this.dateButton, !locked);

        this.analyticAccountDropdown.title = ReconciliationEntryStateHelper.escape(this._item.analyticAccountName ?? this._item.analyticAccount?.title ?? MIOLocalizeString("SELECT_ANALYTIC_ACCOUNT", "SELECT ANALYTIC ACCOUNT"));
        this.setButtonEnabled(this.analyticAccountDropdown, !locked);

        this.updateLinkBox();
        this.updateDifferences();

        this.fetchedResultsController = null;
        this.calculateRemainingAmount();
        this.tableView.reloadData();
        this.updateEntryState();
    }

    // The entry's date; before there is an entry, the one picked or the document's
    private entryDate() : Date {
        return this._journal_entry?.date ?? this.conciliation_date ?? this._item?.valueForKey("date") ?? MIODateToday();
    }

    private setButtonEnabled(button:MUIButton, enabled:boolean) {
        button.enabled = enabled;
        if (enabled) MUICoreLayerRemoveStyle(button.layer, "disable");
        else MUICoreLayerAddStyle(button.layer, "disable");
    }

    // The link box. A bank movement shows the documents it is linked with, their total, the
    // customers or suppliers behind them and the differences; an invoice shows itself.
    private updateLinkBox() {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let escape = ReconciliationEntryStateHelper.escape;
        let locked = this.entryState == ReconciliationEntryState.locked;

        if (!(this._item instanceof BankMovement)) {
            // An invoice's entry is linked to that invoice only (generating the entry, or adding
            // its first line by hand, links it): until then the invoice is not linked
            let document = this.accountingSource as DBDocument;
            let linked = document != null && this.isJournalEntryLinkedTo(document);
            let number = document?.documentNumber ?? "";
            this.linkTitleLabel.text = linked ? MIOLocalizeString("LINK_WITH_...", "Link with...") : MIOLocalizeString("NOT_LINKED", "Not linked");
            this.linkEntityLabel.text = linked ? escape((this.documentKindTitle(document) + " " + number).trim()) : "";
            this.linkAmountLabel.text = linked ? ad.currencyFormatter.stringFromNumber(document.totalAmount ?? 0) : null;
            this.linkLegalEntityLabel.text = linked ? escape(ReconciliationEntryStateHelper.legalEntityOfDocument(document)?.name) : "";
            this.viewLinksButton.hidden = true;
            this.linkButton.hidden = true;
            // only bank movements are linked with documents: an invoice has no link selection
            this.collapseLinkSelection();
            this.footerCotainer.hidden = true;
            return;
        }

        let links = this.documentLinks();
        let linksSummary = this.documentLinksSummary(links);
        this.linkEntityLabel.text = escape(linksSummary?.length > 0 ? linksSummary : (this._item.journalEntry as any)?.linkedEntityConcept);

        let documentLinksCount = links.filter(l => l.linkedEntityName != "BankMovementConcept").length;
        this.linkAmountLabel.text = documentLinksCount > 0 ? ad.currencyFormatter.stringFromNumber(this.documentsTotal()) : null;

        let isLinked = links.length > 0 || this.linkEntityLabel.text?.length > 0;
        this.linkTitleLabel.text = isLinked ? MIOLocalizeString("LINK_WITH_...", "Link with...") : MIOLocalizeString("NOT_LINKED", "Not linked");
        this.linkLegalEntityLabel.text = escape(this.linkedLegalEntitiesSummary(links));
        this.viewLinksButton.hidden = links.length == 0;
        this.linkButton.hidden = locked;
        this.footerCotainer.hidden = false;
        if (locked) this.collapseLinkSelection();
    }

    // The journal entry is filed with the document (linkedEntityID), as the server files the
    // entries it generates
    private isJournalEntryLinkedTo(document:CoreAccounting) : boolean {
        let linkedID = this._journal_entry?.linkedEntityID;
        if (linkedID == null) return false;
        return String(linkedID).toLowerCase() == String(document.identifier).toLowerCase();
    }

    private documentKindTitle(document:DBDocument) : string {
        if (document instanceof SalesInvoice) return MIOLocalizeString("SALES_INVOICE", "Sales invoice");
        if (document instanceof PurchaseInvoice) return MIOLocalizeString("PURCHASE_INVOICE", "Purchase invoice");
        return "";
    }

    private conceptLinks() : BankMovementDocumentLink[] {
        return this.documentLinks().filter(l => l.linkedEntityName == "BankMovementConcept");
    }

    private documentsTotal() : number {
        let total = 0;
        for (let link of this.documentLinks()) {
            if (link.linkedEntityName == "BankMovementConcept") continue;
            total += link.amount ?? 0;
        }
        return total;
    }

    //
    // Differences tracked with concept links, shown as red lines under the
    // linked legal entity name. Info only: the accountant adds the matching
    // entry line from this information.
    //
    private differenceRows:MUIView[] = [];
    private updateDifferences() {
        for (let row of this.differenceRows) row.removeFromSuperview();
        this.differenceRows = [];

        let links = this.conceptLinks();
        this.differencesView.hidden = links.length == 0;
        if (links.length == 0) return;

        let cf = (MUIWebApplication.sharedInstance().delegate as AppDelegate).currencyFormatter;

        // Concept at the left, amount at the right: the same shape as the
        // documents line above, so the amounts line up in one column
        for (let link of links) {
            let row = new MUIView();
            row.init();
            row.layer.style.position = "relative";
            MUICoreLayerAddStyle(row.layer, "link-line");

            let conceptLabel = new MUILabel();
            conceptLabel.init();
            conceptLabel.layer.style.position = "relative";
            MUICoreLayerAddStyle(conceptLabel.layer, "label");
            MUICoreLayerAddStyle(conceptLabel.layer, "small");
            MUICoreLayerAddStyle(conceptLabel.layer, "alert");
            conceptLabel.text = link.concept;
            row.addSubview(conceptLabel);

            let amountLabel = new MUILabel();
            amountLabel.init();
            amountLabel.layer.style.position = "relative";
            MUICoreLayerAddStyle(amountLabel.layer, "label");
            MUICoreLayerAddStyle(amountLabel.layer, "small");
            MUICoreLayerAddStyle(amountLabel.layer, "alert");
            MUICoreLayerAddStyle(amountLabel.layer, "align-right");
            amountLabel.text = cf.stringFromNumber(link.amount);
            row.addSubview(amountLabel);

            this.differencesView.addSubview(row);
            this.differenceRows.push(row);
        }
    }

    numberOfSections(tableView:UITableView) {
        return 3;
    }

    numberOfRowsInSection(tableView:UITableView, section:number) {
        let lines = this.fetchedResultsController.fetchedObjects.length;
        switch (section) {
            case AccountingEntryViewController.linesSection:  return lines;
            case AccountingEntryViewController.totalsSection: return lines > 0 ? 1 : 0;
            // a line can be added until the entry is locked, also before there is one
            default: return this.entryState != ReconciliationEntryState.locked ? 1 : 0;
        }
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath)
    {
        if (indexPath.section == AccountingEntryViewController.totalsSection) {
            let totalsCell = tableView.dequeueReusableCellWithIdentifier( "TotalsAccountingEntryCell" ) as AccountingEntryTotalsCell;
            let totals = this.entryTotals();
            totalsCell.setTotals(totals.debit, totals.credit);
            return totalsCell;
        }

        let cell:AccountingEntryCell = null;

        if (indexPath.section == AccountingEntryViewController.addSection)
        {
            cell = tableView.dequeueReusableCellWithIdentifier( "AddAccountingEntryCell" ) as AccountingEntryCell;
            cell.delegate = this;
            if (this.fetchedResultsController.fetchedObjects.length == 0) {
                let acc = this._item instanceof BankMovement ? this._item.bankAccount.ledgerAccount : null;
                let amount = this._item instanceof BankMovement ? this._item.amount : 0;
                cell.updateValues( amount, acc );
            }
            else {
                cell.amount = this._remainig_amount;
            }
            cell.updateReference( this.defaultReference() );

        }
        else {            
            if (this.entryState != ReconciliationEntryState.locked) {
                cell = tableView.dequeueReusableCellWithIdentifier( "EditAccountingEntryCell" ) as AccountingEntryCell;
            }
            else {
                cell = tableView.dequeueReusableCellWithIdentifier( "InfoAccountingEntryCell" ) as AccountingEntryCell;
            }
            cell.delegate = this;
            let item = this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(indexPath.row, 0)) as AccountingEntry;
            cell.item = item;

        }

        return cell;
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        if (indexPath.section == AccountingEntryViewController.addSection) return UITableViewCellEditingStyle.Insert;
        if (indexPath.section == AccountingEntryViewController.totalsSection) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView:UITableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath) {

        if (editingStyle == UITableViewCellEditingStyle.Delete) {
            let item = this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(indexPath.row, 0));
            this.deleteEntry(item);

        }
        else if (editingStyle == UITableViewCellEditingStyle.Insert) {
            let cell = tableView.cellAtIndexPath(indexPath) as AccountingEntryCell;
            this.addEntryDidPress(cell);
        }
    } 

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
    
        this._fetchedResultsController = value;
    }

    get fetchedResultsController()
    {
        if (this._item == null) return null;
        if (this._fetchedResultsController != null) return this._fetchedResultsController;            
        
        let ad = MUIWebApplication.sharedInstance().delegate;

        let sortDescriptors = [
            MIOSortDescriptor.sortDescriptorWithKey('orderKey', true)
        ];
        
        // Entries added by hand hang on the item; the ones the rules generate hang on
        // the document, so a selected line also lists its invoice's entries
        let predicateFormat = "(relationEntityID == " + this._item.identifier + " AND relationEntityName == '" + this._item.entity.name + "')";
        let source = this.accountingSource;
        if (source != null && source != this._item) {
            predicateFormat += " OR (relationEntityID == " + source.identifier + " AND relationEntityName == '" + source.entity.name + "')";
        }
        // and the ones of the invoice's lines booked one by one
        for (let line of this._legacy_lines) {
            predicateFormat += " OR (relationEntityID == " + line.identifier + " AND relationEntityName == '" + line.entity.name + "')";
        }
        let fetchRequest = DBHelper.listFetchRequestWithEntityName( "AccountingEntry", sortDescriptors, predicateFormat );
        fetchRequest.relationshipKeyPathsForPrefetching = ["account", "journalEntry"];
        
        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;
    
        fetchedResultsController.performFetch();
    
        this._fetchedResultsController = fetchedResultsController;    
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller:MIOFetchedResultsController) {        
        this.calculateRemainingAmount();
        
        this.tableView.reloadData();
        this.updateEntryState();
    }

    private calculateRemainingAmount()
    {
        //TODO: remove this calculation and use the balance reference
        this._remainig_amount = 0;
        
        for( let e of this.fetchedResultsController.fetchedObjects as AccountingEntry[] ) {
            this._remainig_amount += (e.debitAmount - e.creditAmount);
        }
        
    };

    private entryTotals() : { debit:number, credit:number } {
        let debit = 0;
        let credit = 0;
        for (let e of (this._fetchedResultsController?.fetchedObjects ?? []) as AccountingEntry[]) {
            debit += e.debitAmount ?? 0;
            credit += e.creditAmount ?? 0;
        }
        return { debit: debit, credit: credit };
    }

    // The toolbar and the status bar follow the entry's state: lock a balanced draft, unlock a
    // locked entry; the status bar says which one it is, or by how much a draft is unbalanced.
    private updateEntryState() {
        if (this._item == null || this.viewIsLoaded == false) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let state = this.entryState;
        let totals = this.entryTotals();
        let difference = totals.debit - totals.credit;
        let balanced = ReconciliationEntryStateHelper.isBalanced(difference);
        let hasLines = (this._fetchedResultsController?.fetchedObjects.length ?? 0) > 0;

        // a document without entry says what generating it does
        if (state == ReconciliationEntryState.none) MUICoreLayerRemoveStyle(this.emptyView.layer, "hidden");
        else MUICoreLayerAddStyle(this.emptyView.layer, "hidden");
        this.setButtonEnabled(this.generateButton, !this._checking_legacy_lines);

        this.lockButton.hidden = state != ReconciliationEntryState.draft;
        this.unlockButton.hidden = state != ReconciliationEntryState.locked;
        this.setButtonEnabled(this.lockButton, balanced && hasLines);
        if (!hasLines) this.lockButton.layer.title = MIOLocalizeString("THERE_ARE_NO_ENTRIES_TO_LOCK", "There are no entries to lock");
        else this.lockButton.layer.title = balanced ? "" : MIOLocalizeString("BALANCE_THE_JOURNAL_ENTRY_TO_LOCK_IT", "Balance the journal entry to lock it");
        this.setButtonEnabled(this.optionsButton, state != ReconciliationEntryState.locked);

        // the totals of the lines as they are being edited
        let totalsCell = this.tableView.cellAtIndexPath(MIOIndexPath.indexForRowInSection(0, AccountingEntryViewController.totalsSection)) as AccountingEntryTotalsCell;
        totalsCell?.setTotals(totals.debit, totals.credit);

        // the dot has the list's colours (red without entry, yellow while it is a draft, cyan once
        // locked); an unbalanced draft is red, and only its message, to be fixed, is red too
        let journalIndex = this._journal_entry?.journalIndex;
        let style = ReconciliationEntryStateHelper.styleForState(state);
        let alertText = false;
        let text:string = null;
        if (state == ReconciliationEntryState.none) {
            text = MIOLocalizeString("DOCUMENT_PENDING_ACCOUNTING", "Document pending accounting") + ".";
        }
        else if (state == ReconciliationEntryState.locked) {
            let entry = journalIndex != null ? MIOLocalizeString("JOURNAL_ENTRY_N", "Journal entry Nº") + " " + journalIndex : MIOLocalizeString("THE_JOURNAL_ENTRY", "The journal entry");
            text = entry + " " + MIOLocalizeString("LOCKED_UNLOCK_IT_TO_EDIT_IT", "locked. Unlock it to edit it") + ".";
        }
        else if (!balanced) {
            style = "alert";
            alertText = true;
            text = MIOLocalizeString("UNBALANCED_JOURNAL_ENTRY_DIFFERENCE", "Unbalanced journal entry · difference") + " " + ad.currencyFormatter.stringFromNumber(Math.abs(difference)) + ". " + MIOLocalizeString("FIX_THE_LINES_BEFORE_LOCKING", "Fix the lines before locking") + ".";
        }
        else {
            let draft = journalIndex != null ? MIOLocalizeString("DRAFT_N", "Draft Nº") + " " + journalIndex : MIOLocalizeString("DRAFT", "Draft");
            text = draft + " · " + MIOLocalizeString("BALANCED_JOURNAL_ENTRY", "balanced journal entry") + ".";
        }

        for (let s of ["alert", "caution", "active"]) MUICoreLayerRemoveStyle(this.statusDot.layer, s);
        MUICoreLayerAddStyle(this.statusDot.layer, style);
        this.statusLabel.text = ReconciliationEntryStateHelper.escape(text);
        if (alertText) MUICoreLayerAddStyle(this.statusLabel.layer, "alert");
        else MUICoreLayerRemoveStyle(this.statusLabel.layer, "alert");
    }

    // The list shows the item's new state
    private postEntryStateDidChange(item:CoreAccounting = null) {
        let source = item ?? this.accountingSource;
        if (source == null) return;
        MIONotificationCenter.defaultCenter().postNotification(ReconciliationEntryStateHelper.didChangeNotification, source);
    }

    private lockEntries(){
        if (this._journal_entry == null || this.fetchedResultsController.fetchedObjects.length == 0) {
            AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR","ERROR"), MIOLocalizeString("THERE_ARE_NO_ENTRIES_TO_LOCK", "THERE ARE NO ENTRIES TO LOCK"));
            return;
        }

        this.calculateRemainingAmount();

        if (!ReconciliationEntryStateHelper.isBalanced(this._remainig_amount)) {
            AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR","ERROR"), MIOLocalizeString("THE_ENTRIES_ARE_NOT_BALANCED", "THE ENTRIES ARE NOT BALANCED"));
            return;
        }

        for (let je of this.involvedJournalEntries()) je.locked = true;
        this._item.journalEntryLocked = true;
        let source = this.accountingSource;
        if (source != null) source.journalEntryLocked = true;
        for (let line of this._legacy_lines) line.journalEntryLocked = true;

        DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
            this.updateUI();
            this.postEntryStateDidChange();
        });
    }

    private unlockEntries(){
        for (let je of this.involvedJournalEntries()) je.locked = false;
        this._item.journalEntryLocked = false;
        let source = this.accountingSource;
        if (source != null) source.journalEntryLocked = false;
        for (let line of this._legacy_lines) line.journalEntryLocked = false;

        DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
            this.updateUI();
            this.postEntryStateDidChange();
        });
    }

    private showDatePicker(){
        if (this._item == null || this.entryState == ReconciliationEntryState.locked) return;

        let dp = new MUIDatePickerController();
        dp.init();
        dp.delegate = this;
        dp.modalPresentationStyle = MUIModalPresentationStyle.Popover;

        let pc = dp.popoverPresentationController;
        pc.sourceRect = this.dateButton.frame;
        pc.sourceView = this.dateButton;

        AppHelper.sharedInstance().presentViewController(dp, true);
    }

    // the entry takes the date; before there is one, it is kept for when it is created
    didSelectDate(datePickerController:MUIDatePickerController, date:Date) {
        if (date == null || this.entryState == ReconciliationEntryState.locked) return;

        this.conciliation_date = date;
        if (this._journal_entry != null) {
            this._journal_entry.date = date;
            DBHelper.saveMainContext();
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.dateTextField.text = ad.dateFormatter.stringFromDate(date);
    }

    private showSelectAnalyticAccountViewController(){
        if (this._item == null || this.entryState == ReconciliationEntryState.locked) return;

        AppHelper.sharedInstance().showSelectAnalyticAccountViewControllerFromView(this.analyticAccountDropdown, this._item.analyticAccount, this, function(this:AccountingEntryViewController, controller:SelectEntityViewController, account:AnalyticAccount){
            this._item.analyticAccount = account;
            this._item.analyticAccountName = account?.title;
            DBHelper.saveMainContext();
            this.updateUI();
        });
    }

    private documentLinks() : BankMovementDocumentLink[] {
        if (!(this._item instanceof BankMovement)) return [];

        let links = (this._item.documentLink?.allObjects ?? []) as BankMovementDocumentLink[];
        return links.sort(function(a, b){
            if (a.linkedEntityName == b.linkedEntityName) return (a.concept ?? "") < (b.concept ?? "") ? -1 : 1;
            return a.linkedEntityName < b.linkedEntityName ? -1 : 1;
        });
    }

    // "SALES INVOICE F-001, F-002 · PAYROLL 2026-06" -> type of the link plus the
    // document numbers. Concept links are left out: they render as difference lines
    private documentLinksSummary(links:BankMovementDocumentLink[]) : string {
        let documentsByType = {};
        let types = [];
        for (let link of links) {
            if (link.linkedEntityName == "BankMovementConcept") continue;
            let type = BankMovementLinkCell.displayNameForLinkedEntity(link.linkedEntityName);
            if (documentsByType[type] == null) { documentsByType[type] = []; types.push(type); }
            let name = link.documentNumber?.length > 0 ? link.documentNumber : link.concept;
            if (name?.length > 0) documentsByType[type].push(name);
        }

        return types.map(t => documentsByType[t].length > 0 ? t + " " + documentsByType[t].join(", ") : t).join(" · ");
    }

    // Unique legal entities behind the links, e.g. the client or supplier of a linked invoice
    private linkedLegalEntitiesSummary(links:BankMovementDocumentLink[]) : string {
        let names = [];
        for (let link of links) {
            let name = link.legalEntityName ?? link.legalEntity?.name;
            if (name?.length > 0 && names.indexOf(name) == -1) names.push(name);
        }
        return names.join(", ");
    }

    // Default entry reference: on an invoice the one the generated lines carry (the supplier's
    // number on a purchase invoice, else the document's); with documents linked to a bank
    // movement, the analytic account's name and the documents' numbers
    private defaultReference() : string {
        let source = this.accountingSource;
        if (source instanceof DBDocument) return ReconciliationEntryStateHelper.referenceOfDocument(source);

        let links = this.documentLinks();
        if (links.length == 0) return null;

        let parts = [];

        let analytic = this._item.analyticAccountName ?? this._item.analyticAccount?.title;
        if (analytic?.length > 0) parts.push(analytic);

        let numbers = links.map(l => l.documentNumber).filter(n => n?.length > 0);
        if (numbers.length > 0) parts.push(numbers.join(", "));

        return parts.length > 0 ? parts.join(" ") : null;
    }

    private showDocumentLinks(){
        let links = this.documentLinks();
        if (links.length == 0) return;

        let vc = DocumentLinkListViewController.newInstance();
        vc.setLinks(links);
        vc.modalPresentationStyle = MUIModalPresentationStyle.Popover;

        let pc = vc.popoverPresentationController;
        pc.sourceRect = this.viewLinksButton.frame;
        pc.sourceView = this.viewLinksButton;

        this.presentViewController(vc, true);
    }

    // The link footer closes when its button goes away (an invoice, a locked entry)
    private collapseLinkSelection() {
        if (!this.showSelectedEntityViewController || this.accountingSelectionEntityViewController == null) return;
        this.accountingSelectionEntityViewController.view.setHeight(0);
        this.showSelectedEntityViewController = false;
    }

    private showSelectedEntityViewController = false;
    private showSelectEntityViewController(){
        MUIView.animateWithDuration(0.75, this, function(this:AccountingEntryViewController){
            this.accountingSelectionEntityViewController.view.setHeight( this.showSelectedEntityViewController ? 0 : 400);
        }, function(){
            this.showSelectedEntityViewController = !this.showSelectedEntityViewController;
            this.accountingSelectionEntityViewController.view.setNeedsDisplay();
        });

    }

    private selectAccountEntryTypeAction(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ACTIONS","ACTIONS"), MIOLocalizeString("CHOOSE_A_TYPE", "CHOOSE A TYPE"), MUIAlertViewStyle.Default);
 
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SALES_INVOICE", "SALES INVOICE"), MUIAlertActionStyle.Default, this, function(){
            this.selectEntityViewController(AccountEntintyType.SalesInvoice);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("PURSCHASE_INVOICE", "PURSCHASE INVOICE"), MUIAlertActionStyle.Default, this, function(){
            this.selectEntityViewController(AccountEntintyType.PurchaseInvoice);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CLIENT","CLIENT"), MUIAlertActionStyle.Default, this, function(){
            this.selectEntityViewController(AccountEntintyType.Client);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SUPPLIER","SUPPLIER"), MUIAlertActionStyle.Default, this, function(){
            this.selectEntityViewController(AccountEntintyType.Supplier);
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private selectEntityViewController(type:AccountEntintyType){
        switch (type) 
        {   
            case AccountEntintyType.SalesInvoice:
                this.showSelectDocumentViewController(SelectDocumentViewControllerType.salesInvoice);
                break;

            case AccountEntintyType.PurchaseInvoice:
                this.showSelectDocumentViewController(SelectDocumentViewControllerType.purchaseInvoice);
                break;    

            case AccountEntintyType.Client:
                AppHelper.sharedInstance().showSelectClientViewControllerFromView(null, null, true, this, this.addClientAccountEntry, null, null);
                break;

            case AccountEntintyType.Supplier:
                AppHelper.sharedInstance().showSelectSupplierViewControllerFromView(null, null, true, this, this.addSupplierAccountEntry);
                break;
        }
    }

    private showSelectDocumentViewController(type:SelectDocumentViewControllerType){
        let vc = SelectDocumentViewController.newInstance();
        vc.documentType = type;
        this.presentViewController(vc, true);
    }

    addClientAccountEntry(controller:any, client:Client){
        let account = client.ledgerAccount;
        (this as any).addLedgerAccountEntry(controller, account);
    }

    addSupplierAccountEntry(controller:any, supplier:Supplier){
        let account = supplier.ledgerAccount;
        (this as any).addLedgerAccountEntry(controller, account);
    }

    addPurchaseInvoiceAccountEntry(controller:any, purchaseInvoices:PurchaseInvoice[]){
        let supplier = (purchaseInvoices[0] as any).supplier;
        (this as any).addLedgerAccountEntry(null, supplier.ledgerAccount);

        for (let i = 0; i < purchaseInvoices.length; i++){
            let pi = purchaseInvoices[i];
            (this as any).addLedgerAccountEntry(null, pi.ledgerAccount);
        }
    }

    linkInstalmentsFromBankFile(sender:any, file:BankTransferBundleFile){
        DBHelper.queryObjectsWithCompletion("LegalEntityPaymentInstallment", null, MIOPredicate.predicateWithFormat( "operationReference == '" + file.reference + "'" ), ["legalEntity.ledgerAccount", "legalEntity.salesLedgerAccount", "legalEntity.purchaseLedgerAccount"], this, function(this:AccountingEntryViewController, objects:LegalEntityPaymentInstallment[]){
            if (objects.length == 0) return;
            let objs = _MIOSortDescriptorSortObjects(objects, [MIOSortDescriptor.sortDescriptorWithKey("documentNumber", true) ] );
            
            try {
                // Do the checks before saving anything.
                this.checkInstallments(objs);
            }
            catch (error) {
                console.log("ERROR: " + error);
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), error.message);
            }

            for (let ins of objs) {
                this.addInstallmentAccountEntry(sender, ins, false);
            }
                
            file.journalEntry = this._journal_entry;
            (this._journal_entry as any).linkedPropertyName = "bankFile";
            (this._journal_entry as any).linkedEntityConcept = "FILE - " + file.reference;
            // the movement has an entry now: the panel and the list leave the pending state
            DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
                this.updateUI();
                this.postEntryStateDidChange();
            });
        });
    }

    private checkInstallments(installments:PaymentInstallment[]){
        for (let ins of installments) {

            if (!(ins instanceof LegalEntityPaymentInstallment)) throw new Error(MIOLocalizeString("INSTALLMENT_IS_NOT_A_LEGAL_ENTITY_PAYMENT_INSTALLMENT: ", "INSTALLMENT IS NOT A LEGAL ENTITY PAYMENT INSTALLMENT"));

            let le = ins.legalEntity;
            if (le instanceof Client && ins.legalEntity.salesLedgerAccount == null) {
                throw new Error(MIOLocalizeString("SALES_LEDGER_ACCOUNT_IS_NULL_FOR_CLIENT: ","SALES LEDGER ACCOUNT IS NULL FOR CLIENT: ") + le.name);
            }
            else if (le instanceof Supplier && ins.legalEntity.purchaseLedgerAccount == null) {
                throw new Error(MIOLocalizeString("PURCHASE_LEDGER_ACCOUNT_IS_NULL_FOR_SUPPLIER: ","PURCHASE LEDGER ACCOUNT IS NULL FOR SUPPLIER: ") + le.name);                
            }
            else {
                throw new Error(MIOLocalizeString("LEGAL_ENTITY_NOT_IMPLEMENTED_FOR_INSTALLMENT: ", "LEGAL ENTITY NOT IMPLEMENTED FOR INSTALLMENT: ") + le.entity.name);
            }
        }
    }

    addInstallmentAccountEntry(sender: any, installment: LegalEntityPaymentInstallment, save:boolean): void {
        let le = installment.legalEntity;
        if (le instanceof Client) {
            this.newLedgerAccountEntry(installment.legalEntity.salesLedgerAccount, installment.documentNumber, installment.amount, 0, save);
        }
        else if (le instanceof Supplier) {
            this.newLedgerAccountEntry(installment.legalEntity.purchaseLedgerAccount, installment.documentNumber, 0, installment.amount, save);
        }
    }        

    private addLedgerAccountEntry(account:LedgerAccount, reference:string, amount:number, save:boolean = true) {
        let entry = this.newLedgerAccountEntry(account, reference, 0, 0, save);
        entry.setAmount( amount, account );
        if (save) DBHelper.saveMainContext();
    }

    private newLedgerAccountEntry(account:LedgerAccount, reference:string, creditAmount:number, debitAmount:number, save:boolean = true) : AccountingEntry 
    {
        if ( !( this._item instanceof CoreAccounting ) ) return;
        if ( account == null ) throw new Error(MIOLocalizeString("TRYING_TO_ADD_NULL_ACCOUNTING_ENTRY", "TRYING TO ADD NULL ACCOUNTING ENTRY"));

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        // journal_entry is not-null in the DB, so make sure the journal entry exists before adding entries.
        // It is filed as the server files the generated ones, so generating it later reuses it.
        let createdJournalEntry = false;
        if (this._journal_entry == null) {
            let source = this.accountingSource ?? this._item;
            this._journal_entry = MIOEntityDescription.insertNewObjectForEntityForName( "AccountingJournalEntry", ad.managedObjectContext ) as AccountingJournalEntry;
            this._journal_entry.date = this.conciliation_date ?? this._item.valueForKey("date") ?? new Date();
            this._journal_entry.linkedEntityID = source.identifier;
            this._journal_entry.linkedEntityName = this.journalEntityName(source);
            this._item.journalEntry = this._journal_entry;
            if (source != this._item) source.journalEntry = this._journal_entry;
            createdJournalEntry = true;
        }

        let entry = MIOEntityDescription.insertNewObjectForEntityForName( "AccountingEntry", ad.managedObjectContext ) as AccountingEntry;
        
        let defaultReference:string = null;
        if (this._item instanceof BankMovement) {
            entry.date = this._item.date;
        }
        else if (this._item instanceof SalesInvoiceLine || this._item instanceof PurchaseInvoiceLine) {
            entry.date = this._item.document.date;
            defaultReference = ReconciliationEntryStateHelper.referenceOfDocument(this._item.document);
        }
        else if (this._item instanceof DBDocument) {
            entry.date = this._item.date;
            defaultReference = ReconciliationEntryStateHelper.referenceOfDocument(this._item);
        }

        entry.relationEntityName = this._item.entity.name;
        entry.relationEntityID = this._item.identifier;
        entry.account = account;
        entry.accountNumber = account.prefix;
        entry.accountName = account.name;        
        entry.reference = reference ?? defaultReference;
        entry.debitAmount = debitAmount;
        entry.creditAmount = creditAmount;
        entry.orderKey = DBHelper.newSafeTimestamp();
        entry.journalEntry = this._journal_entry;

        if (save) {
            DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
                if (!createdJournalEntry) return;
                // the document has an entry now: the panel and the list leave the pending state
                this.updateUI();
                this.postEntryStateDidChange();
            });
        }

        console.log("ACCOUNTING ENTRY: " + reference + " - debit: " + debitAmount + " - credit: " + creditAmount);
        // this._item.accountingBalance = this._remainig_amount;
        // this._item.accountingEntries = this.fetchedResultsController.fetchedObjects.length;

        return entry;
    }

    // The source's name for the server (AccountingEventType.entityName in DualLinkDB)
    private journalEntityName(source:CoreAccounting) : string {
        if (source instanceof SalesInvoice) return "SalesInvoice";
        if (source instanceof PurchaseInvoice) return "PurchaseInvoice";
        if (source instanceof BankMovement) return "BankEntry";
        return source?.entity?.name ?? null;
    }

    private templateTypeForSource(source:CoreAccounting) : string {
        if (source instanceof SalesInvoice) return "sales-invoice";
        if (source instanceof PurchaseInvoice) return "purchase-invoice";
        if (source instanceof BankMovement) return "bank-entry";
        return null;
    }

    private deleteEntry(entry:AccountingEntry){
        DBHelper.deleteObjectFromMainContext(entry, true);

        // this._item.accountingBalance = this._remainig_amount;
        // this._item.accountingEntries = this.fetchedResultsController.fetchedObjects.length;
    }

    addEntryDidPress(cell: AccountingEntryCell): void {
        if (cell?.account == null) {
            AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR","ERROR"), MIOLocalizeString("SELECT_LEDGER_ACCOUNT", "Select ledger account"));
            return;
        }
        this.newLedgerAccountEntry(cell.account, cell.reference, cell.creditAmount, cell.debitAmount);
    }

    amountDidUpdate(cell: AccountingEntryCell): void {
        this.calculateRemainingAmount();
        // this._item.accountingBalance = this._remainig_amount;        

        // Update add cell with the remaining amount
        let index_path = MIOIndexPath.indexForRowInSection(0, AccountingEntryViewController.addSection);
        let add_cell = this.tableView.cellAtIndexPath(index_path) as AccountingEntryCell;
        if (add_cell) add_cell.amount = this._remainig_amount;

        this.updateEntryState();
    }

    // A customer's or supplier's account shows the party's name: the accounts of the invoice's
    // party or of the parties of the documents linked to a bank movement, and on an invoice any
    // account of the customers' and suppliers' groups
    displayNameForAccount(account:LedgerAccount, accountName:string) : string {
        let name = accountName ?? account?.name;
        if (account == null) return name;

        let source = this.accountingSource;
        let parties:LegalEntity[] = [];
        if (this._item instanceof BankMovement) {
            for (let link of this.documentLinks()) {
                if (link.legalEntity != null && parties.indexOf(link.legalEntity) == -1) parties.push(link.legalEntity);
            }
        }
        else {
            let legalEntity = ReconciliationEntryStateHelper.legalEntityOfDocument(source);
            if (legalEntity != null) parties.push(legalEntity);
        }

        for (let party of parties) {
            if (ReconciliationEntryStateHelper.partyAccountsOfLegalEntity(party).indexOf(account) >= 0) return party.name ?? name;
        }

        if (!(this._item instanceof BankMovement) && parties.length == 1 && ReconciliationEntryStateHelper.isPartyAccountNumber(account.prefix)) {
            return parties[0].name ?? name;
        }

        return name;
    }

    // Generate while there is no entry; regenerate (it replaces the lines) or discard a draft
    private optionsAction(){
        let state = this.entryState;
        if (this._item == null || state == ReconciliationEntryState.locked) return;

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("ACTIONS","ACTIONS"), null, MUIAlertViewStyle.Default);
 
        if (state == ReconciliationEntryState.none) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("GENERATE_JOURNAL_ENTRY", "Generate journal entry"), MUIAlertActionStyle.Default, this, function(this:AccountingEntryViewController){
                this.generateEntries();
            }));
        }
        else {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("REGENERATE_JOURNAL_ENTRY", "Regenerate journal entry"), MUIAlertActionStyle.Default, this, function(this:AccountingEntryViewController){
                this.confirmRegeneration();
            }));
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("DISCARD", "Discard"), MUIAlertActionStyle.Destructive, this, function(this:AccountingEntryViewController){
                this.confirmDiscard();
            }));
        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    /// A ledger account or classification of the item changed in the reconciliation list: it is
    /// saved, and a draft entry is offered to be generated again with it (a locked one is kept).
    accountsDidChangeForItem(item:CoreAccounting) {
        DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
            if (item != this._item && item != this.accountingSource) return;
            if (this.entryState != ReconciliationEntryState.draft || this._regeneration_declined) return;
            this.confirmRegeneration(true);
        });
    }

    private regenerationAlertIsPresented = false;
    private confirmRegeneration(afterChange:boolean = false) {
        if (this.regenerationAlertIsPresented) return;
        this.regenerationAlertIsPresented = true;

        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("REGENERATE_JOURNAL_ENTRY", "Regenerate journal entry"), MIOLocalizeString("THE_DRAFT_JOURNAL_ENTRY_WILL_BE_GENERATED_AGAIN_AND_ITS_MANUAL_LINES_REPLACED", "The draft journal entry will be generated again and its manual lines replaced"), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("REGENERATE", "Regenerate"), MUIAlertActionStyle.Default, this, function(this:AccountingEntryViewController){
            this.regenerationAlertIsPresented = false;
            this.generateEntries();
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString(afterChange ? "NOT_NOW" : "CANCEL", afterChange ? "Not now" : "Cancel"), MUIAlertActionStyle.Cancel, this, function(this:AccountingEntryViewController){
            this.regenerationAlertIsPresented = false;
            // the next changes to this document are only saved; the options still regenerate
            if (afterChange) this._regeneration_declined = true;
        }));

        this.presentViewController(avc, true);
    }

    private confirmDiscard() {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("DISCARD_THE_JOURNAL_ENTRY", "Discard the journal entry"), MIOLocalizeString("ITS_LINES_WILL_BE_DELETED", "Its lines will be deleted"), MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("DISCARD", "Discard"), MUIAlertActionStyle.Destructive, this, function(this:AccountingEntryViewController){
            this.discardEntries();
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL","CANCEL"), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    // Deletes the draft entry with its lines: the document is pending again
    private discardEntries() {
        if (this.entryState != ReconciliationEntryState.draft) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let moc = ad.managedObjectContext;

        let journalEntries = this.involvedJournalEntries();
        for (let entry of (this.fetchedResultsController.fetchedObjects ?? []) as AccountingEntry[]) moc.deleteObject(entry);
        for (let je of journalEntries) moc.deleteObject(je);

        let source = this.accountingSource;
        this._item.journalEntry = null;
        this._item.journalEntryLocked = false;
        if (source != null && source != this._item) {
            source.journalEntry = null;
            source.journalEntryLocked = false;
        }
        for (let line of this._legacy_lines) {
            line.journalEntry = null;
            line.journalEntryLocked = false;
        }
        this._legacy_lines = [];
        this._journal_entry = null;
        this._regeneration_declined = false;

        // The panel empties with the save; it refetches only once the server has the deletes,
        // or it would get the discarded lines back from the server
        DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
            if (source == this.accountingSource) this.updateUI();
            this.postEntryStateDidChange(source);
        });
    }

    private generateEntries(){
        let source = this.accountingSource;
        let template_type = this.templateTypeForSource(source);

        if (template_type == null || source == null) {
            AppHelper.showInfoMessage(this, MIOLocalizeString("GENERATE_JOURNAL_ENTRY", "Generate journal entry"), MIOLocalizeString("GENERATION_NOT_AVAILABLE_FOR_THIS_DOMAIN", "GENERATION IS NOT AVAILABLE FOR THIS DOMAIN"));
            return;
        }

        // the server unlocks what it generates: a locked entry is unlocked first, by hand; and the
        // lines booked one by one have to be known before anything replaces them
        if (this.entryState == ReconciliationEntryState.locked || this._checking_legacy_lines) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        // What the new entry replaces: the server empties the source's entry and fills it again,
        // but the context keeps the old lines, and entries made before they were filed with the
        // source (or on the lines one by one) are not the one it refills.
        let replaced = {
            source: source,
            entries: ((this._fetchedResultsController?.fetchedObjects ?? []) as AccountingEntry[]).slice(),
            journalEntries: this.involvedJournalEntries(),
            legacyLines: this._legacy_lines.slice(),
            date: this.conciliation_date
        };

        // the server reads the database: what was changed here is uploaded first
        DBHelper.saveMainContextWithCompletion(this, function(this:AccountingEntryViewController){
            ad.webService.calculateAccountingJournalEntries( template_type, source.identifier, (json:any, error:string) => {
                if (error != null) return;

                // The replaced lines go first: the panel refetches once the server has their
                // deletes, or it would list them again. The entry is persisted even when a rule
                // failed: the panel refetches the generated lines, the journal entry's number and
                // date come in the background, then the errors are reported (the status bar
                // tells the rest).
                this.removeReplacedEntries(json, replaced, () => {
                    let current = source == this.accountingSource;
                    if (current) this.updateUI();
                    this.reloadGeneratedJournalEntry(source, replaced.date);
                    if (!current) return;

                    let errors = ((json?.Errors ?? []) as any[]).filter( e => e != null );
                    let unresolved = errors.filter( e => e.Kind == "unresolvedLedgerAccount" );
                    if (unresolved.length > 0) {
                        this.presentUnresolvedLedgerAccountErrors( source, unresolved );
                        return;
                    }
                    // an unbalanced entry shows in the totals and the status bar; only what the
                    // panel cannot show (a template formula that failed) is told in an alert
                    let hidden = errors.filter( e => e.Kind != "unbalancedJournalEntry" );
                    if (hidden.length > 0) {
                        AppHelper.showInfoMessage(this, MIOLocalizeString("JOURNAL_ENTRY", "JOURNAL ENTRY") + " #" + (json?.JournalIndex ?? ""), ad.webService.journalEntrySummary(json));
                    }
                });
            });
        });
    }

    // Deletes from the context (and so from the database, where the server already did for the
    // lines of the entry it refilled) the lines and journal entries the generated one replaces
    private removeReplacedEntries(json:any, replaced:any, completion:() => void) {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let moc = ad.managedObjectContext;
        let id = (value:any) => String(value ?? "").toLowerCase();

        let kept = ((json?.Entries ?? []) as any[]).map(e => id(e?.ID));
        let journalEntryID = id(json?.JournalEntryID);

        let changed = false;
        for (let entry of replaced.entries as AccountingEntry[]) {
            if (kept.indexOf(id(entry.identifier)) >= 0) continue;
            moc.deleteObject(entry);
            changed = true;
        }
        for (let je of replaced.journalEntries as AccountingJournalEntry[]) {
            if (journalEntryID.length == 0 || id(je.identifier) == journalEntryID) continue;
            moc.deleteObject(je);
            // the panel waits for the new one (reloadGeneratedJournalEntry)
            if (je == this._journal_entry && replaced.source == this.accountingSource) this._journal_entry = null;
            changed = true;
        }
        for (let line of replaced.legacyLines as CoreAccounting[]) {
            line.journalEntry = null;
            line.journalEntryLocked = false;
            changed = true;
        }
        if (replaced.source == this.accountingSource) {
            this._legacy_lines = this._legacy_lines.filter(l => replaced.legacyLines.indexOf(l) < 0);
        }

        if (!changed) { completion(); return; }
        DBHelper.saveMainContextWithCompletion(this, completion);
    }

    // The server attached the journal entry to the source: refetch it so the
    // panel shows the new number, date and lines, and the list the new state.
    // A date picked before generating is given back to the entry (the server
    // dates it as the document).
    private reloadGeneratedJournalEntry(source:CoreAccounting, pickedDate:Date = null) {
        let predicate = MIOPredicate.predicateWithFormat("identifier == '" + source.identifier + "'");
        DBHelper.queryObjectWithCompletion(source.entity.name, null, predicate, ["journalEntry"], this, function(this:AccountingEntryViewController, refreshed:CoreAccounting) {
            let journalEntry = refreshed?.journalEntry ?? source.journalEntry ?? null;
            if (journalEntry != null && pickedDate != null && journalEntry.date?.getTime() != pickedDate.getTime()) {
                journalEntry.date = pickedDate;
                DBHelper.saveMainContext();
            }

            if (source == this.accountingSource) {
                this._journal_entry = journalEntry ?? this._item.journalEntry ?? null;
                this.updateUI();
            }
            this.postEntryStateDidChange(source);
        });
    }

    // The errors of the source's generation, with a retry for that same source
    private presentUnresolvedLedgerAccountErrors( source:CoreAccounting, errors:UnresolvedLedgerAccountError[] ) {
        let vc = UnresolvedLedgerAccountErrorsViewController.newInstance();
        vc.setErrors( source, errors, this, function(this:AccountingEntryViewController){
            if (source == this.accountingSource) this.generateEntries();
        });
        this.presentViewController( vc, true );
    }
}
